// src/components/Dashboard.js - DASHBOARD COMPONENT
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, FileText, Check, Clock, AlertCircle, X,
  CreditCard, Stethoscope, User, LogOut, RefreshCw, Eye, Lock, MessageSquare, Pencil
} from 'lucide-react';

const Dashboard = ({ user, onLogout }) => {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    attention: 0,
    approved: 0,
    denied: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchUserClaims();
  }, [user.member_number]);

  const fetchUserClaims = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get(`/api/members/${user.member_number}/claims/`);
      const userClaims = response.data;
      
      setClaims(userClaims);
      
      // Calculate stats
      const stats = {
        total: userClaims.length,
        pending: userClaims.filter(c => c.status === 'pending' || c.status === 'under_review').length,
        attention: userClaims.filter(c => c.status === 'attention_required').length,
        approved: userClaims.filter(c => c.status === 'approved' || c.status === 'paid').length,
        denied: userClaims.filter(c => c.status === 'denied').length,
      };
      setStats(stats);
      
    } catch (error) {
      console.error('Error fetching claims:', error);
      setError('Failed to load your claims. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const iconProps = { className: 'status-icon', size: 16 };
    switch (status) {
      case 'pending': return <Clock {...iconProps} />;
      case 'under_review': return <Clock {...iconProps} />;
      case 'attention_required': return <AlertCircle {...iconProps} />;
      case 'approved': return <Check {...iconProps} />;
      case 'denied': return <X {...iconProps} />;
      case 'paid': return <Check {...iconProps} />;
      default: return <AlertCircle {...iconProps} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'under_review': return 'status-pending';
      case 'attention_required': return 'status-attention';
      case 'approved': return 'status-approved';
      case 'denied': return 'status-denied';
      case 'paid': return 'status-paid';
      default: return 'status-pending';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount);
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <Building2 className="header-logo" />
            <div className="header-text">
              <h1>Claims Portal</h1>
              <p>Welcome back, {user.full_name}</p>
            </div>
          </div>
          <div className="header-right">
            <div className="user-info">
              <div className="user-details">
                <span className="user-name">{user.full_name}</span>
                <span className="user-member">Member #{user.member_number}</span>
                <span className="user-plan">Plan {user.plan_code}</span>
              </div>
              <User className="user-avatar" />
            </div>
            <button onClick={() => navigate('/change-password')} className="change-password-button">
              <Lock size={18} />
              <span>Change Password</span>
            </button>
            <button onClick={onLogout} className="logout-button">
              <LogOut size={18} />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card total">
            <div className="stat-content">
              <FileText className="stat-icon" />
              <div className="stat-text">
                <div className="stat-number">{stats.total}</div>
                <div className="stat-label">Total Claims</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card pending">
            <div className="stat-content">
              <Clock className="stat-icon" />
              <div className="stat-text">
                <div className="stat-number">{stats.pending}</div>
                <div className="stat-label">Pending</div>
              </div>
            </div>
          </div>

          <div className="stat-card attention">
            <div className="stat-content">
              <AlertCircle className="stat-icon" />
              <div className="stat-text">
                <div className="stat-number">{stats.attention}</div>
                <div className="stat-label">Attention Required</div>
              </div>
            </div>
          </div>

          <div className="stat-card approved">
            <div className="stat-content">
              <Check className="stat-icon" />
              <div className="stat-text">
                <div className="stat-number">{stats.approved}</div>
                <div className="stat-label">Approved</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card denied">
            <div className="stat-content">
              <X className="stat-icon" />
              <div className="stat-text">
                <div className="stat-number">{stats.denied}</div>
                <div className="stat-label">Denied</div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit New Claim */}
        <div className="actions-section">
          <h2>Submit New Claim</h2>
          <div className="claim-types">
            <Link to="/claim/new/dental" className="claim-type-card dental">
              <CreditCard className="claim-type-icon" />
              <div className="claim-type-content">
                <h3>Dental Claim</h3>
                <p>Cleanings, fillings, crowns and other dental procedures</p>
              </div>
            </Link>
            
            <Link to="/claim/new/health" className="claim-type-card health">
              <Stethoscope className="claim-type-icon" />
              <div className="claim-type-content">
                <h3>Extended Health</h3>
                <p>Medical services, prescriptions, equipment and therapy</p>
              </div>
            </Link>
          </div>
        </div>

        {/* Claims History */}
        <div className="claims-section">
          <div className="section-header">
            <h2>Your Claims</h2>
            <button 
              onClick={fetchUserClaims} 
              className="refresh-button"
              disabled={loading}
            >
              <RefreshCw className={loading ? 'spinning' : ''} size={18} />
              <span>Refresh</span>
            </button>
          </div>

          {error && (
            <div className="error-banner">
              <AlertCircle size={20} />
              <span>{error}</span>
              <button onClick={fetchUserClaims} className="retry-button">
                Try Again
              </button>
            </div>
          )}

          {loading ? (
            <div className="loading-claims">
              <RefreshCw className="spinning" size={24} />
              <p>Loading your claims...</p>
            </div>
          ) : claims.length === 0 ? (
            <div className="no-claims">
              <FileText size={48} className="no-claims-icon" />
              <h3>No claims submitted yet</h3>
              <p>Submit your first claim using the options above.</p>
            </div>
          ) : (
            <div className="claims-list">
              {claims.map(claim => (
                <div key={claim.id} className="claim-item">
                  <div className="claim-header">
                    <div className="claim-title">
                      <CreditCard size={18} className="claim-icon" />
                      <span className="claim-type">{claim.claim_type_display} Claim</span>
                      <span className="claim-id">#{claim.id}</span>
                      {claim.status === 'attention_required' && (
                        <span className="attention-badge">
                          <AlertCircle size={13} />
                          Action needed
                        </span>
                      )}
                      {claim.admin_response && claim.status !== 'attention_required' && (
                        <span className="response-badge">
                          <MessageSquare size={13} />
                          Response available
                        </span>
                      )}
                    </div>
                    <div className={`claim-status ${getStatusColor(claim.status)}`}>
                      {getStatusIcon(claim.status)}
                      <span>{claim.status_display}</span>
                    </div>
                  </div>
                  
                  <div className="claim-details">
                    <div className="claim-info">
                      <span className="claim-patient">
                        <strong>Patient:</strong> {claim.patient_name}
                      </span>
                      <span className="claim-date">
                        <strong>Submitted:</strong> {formatDate(claim.submitted_date)}
                      </span>
                      <span className="claim-items">
                        <strong>Services:</strong> {claim.items_count} items
                      </span>
                    </div>
                    <div className="claim-amount">
                      <span className="amount">{formatCurrency(claim.total_amount)}</span>
                    </div>
                  </div>
                  
                  <div className="claim-actions">
                    {(claim.status === 'pending' || claim.status === 'attention_required') && (
                      <button
                        className="edit-claim-button"
                        onClick={() => navigate(`/claim/${claim.id}/edit`)}
                      >
                        <Pencil size={16} />
                        <span>Edit</span>
                      </button>
                    )}
                    <button
                      className="view-claim-button"
                      onClick={() => navigate(`/claim/${claim.id}`)}
                    >
                      <Eye size={16} />
                      <span>View Details</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;